
    import __i18nConfig from '@next-translate-root/i18n.json'
    import __loadNamespaces from 'next-translate/loadNamespaces'
    import { PlayIcon, VolumeOffIcon, VolumeUpIcon } from '@heroicons/react/solid';
import { GetServerSideProps } from 'next';
import dynamic from 'next/dynamic';
import useLocale from 'next-translate/useTranslation';
import React, { useRef, useState } from 'react';
import { Image } from 'types';
import { getPublicVideosPaginated } from '@/data/images';
import Footer from '@/components/layout/Footer';
import ButtonLink from '@/components/links/ButtonLink';
import Seo from '@/components/Seo';
// Above-the-fold hero is imported statically.
import VideoHero from '@/components/videos/landing/VideoHero';
// Below-the-fold sections are code-split.
const VideoShowcaseRows = dynamic(() => import('@/components/videos/landing/VideoShowcaseRows'));
const VideoCapabilities = dynamic(() => import('@/components/videos/landing/VideoCapabilities'));
const VideoModelsBand = dynamic(() => import('@/components/videos/landing/VideoModelsBand'));
const DarkTemplatesGrid = dynamic(() => import('@/components/media/DarkTemplatesGrid'));
const VideoSteps = dynamic(() => import('@/components/videos/landing/VideoSteps'));
const VideoToolLinks = dynamic(() => import('@/components/videos/landing/VideoToolLinks'));
const UserVideos = dynamic(() => import('@/components/videos/UserVideos'));
const VideoFaq = dynamic(() => import('@/components/videos/VideoFaq'));
const FeaturedLogos = dynamic(() => import('@/components/FeaturedLogos'));
const Testimonials = dynamic(() => import('@/components/Testimonials'), {
    ssr: false,
});
const CallToAction = dynamic(() => import('@/components/CallToAction'));
interface VideoGeneratorProps {
    initialVideos: Image[];
}
const VideoGenerator = ({ initialVideos }: VideoGeneratorProps) => {
    const { t } = useLocale('ai-videos');
    const talkingVideoRef = useRef<HTMLVideoElement>(null);
    const [isTalkingVideoMuted, setIsTalkingVideoMuted] = useState(true);
    const [isTalkingVideoPlaying, setIsTalkingVideoPlaying] = useState(true);
    const toggleTalkingVideoMute = (e: React.MouseEvent) => {
        e.stopPropagation();
        if (talkingVideoRef.current) {
            talkingVideoRef.current.muted = !talkingVideoRef.current.muted;
            setIsTalkingVideoMuted(talkingVideoRef.current.muted);
        }
    };
    const toggleTalkingVideoPlay = () => {
        if (talkingVideoRef.current) {
            if (talkingVideoRef.current.paused) {
                talkingVideoRef.current.play().catch(() => { });
                setIsTalkingVideoPlaying(true);
            }
            else {
                talkingVideoRef.current.pause();
                setIsTalkingVideoPlaying(false);
            }
        }
    };
    return (<main className='relative bg-[#07070C]'>
      <Seo templateTitle={t('AI Video Generator | Create Videos from Text & Images')} description={t('Create stunning videos from text or images with our AI technology. Perfect for social media, marketing, and content creation.')} image='https://media.easy-peasy.ai/landing/video/og-video.jpg'/>

      <VideoHero />
      <VideoShowcaseRows />
      <VideoCapabilities />
      <VideoModelsBand />
      <DarkTemplatesGrid kind='video' basePath='/ai-video-generator/templates' heading={t('Start from a proven template')} subheading={t('Viral formats, product ads, explainers and effects — prompts already dialed in.')} showAllLabel={t('Show all templates')}/>

      {/* Featured: AI Talking Videos */}
      <section className='relative py-20 lg:py-28'>
        <div className='mx-auto max-w-7xl px-4 sm:px-6 lg:px-8'>
          <div className='grid grid-cols-1 items-center gap-y-12 gap-x-12 rounded-3xl border border-white/10 bg-white/[0.03] p-8 md:p-12 lg:grid-cols-2'>
            <div>
              <div className='mb-6 inline-flex items-center rounded-full border border-white/15 bg-white/5 px-3 py-1 text-sm font-medium text-slate-200'>
                🔥 {t('Trending Now')}
              </div>
              <h2 className='mb-6 text-3xl font-extrabold tracking-tight text-white sm:text-4xl'>
                {t('NEW AI Talking Videos')}
              </h2>
              <p className='mb-8 text-lg leading-8 text-slate-300'>
                {t('Transform any portrait into a lifelike talking avatar with perfect lip-sync. Ideal for personalized messages, educational content, and social media.')}
              </p>
              <ul className='mb-10 space-y-4'>
                {[
            'Lip-sync matching any audio or text',
            'Natural facial expressions',
            'Support for 40+ languages',
            'High-resolution output',
        ].map((item) => (<li key={item} className='flex items-center text-slate-300'>
                    <svg className='mr-3 h-5 w-5 text-emerald-400' fill='none' viewBox='0 0 24 24' stroke='currentColor'>
                      <path strokeLinecap='round' strokeLinejoin='round' strokeWidth={2} d='M5 13l4 4L19 7'/>
                    </svg>
                    {t(item)}
                  </li>))}
              </ul>
              <ButtonLink href='/ai-video-generator/talking' variant='light' className='rounded-full bg-indigo-500 px-8 py-3 text-base font-semibold text-white shadow-lg hover:bg-indigo-400 border-transparent'>
                {t('Try Talking Videos')}
              </ButtonLink>
            </div>
            <div className='group relative aspect-video w-full cursor-pointer overflow-hidden rounded-2xl border border-white/10 bg-black shadow-2xl' onClick={toggleTalkingVideoPlay}>
              <video ref={talkingVideoRef} className='h-full w-full object-cover' autoPlay loop muted playsInline preload='none' poster='https://media.easy-peasy.ai/assets/01b937f5-9803-4c5a-b68c-925776397072.webp'>
                <source src='https://media.easy-peasy.ai/assets/talking-video.webm' type='video/webm'/>
              </video>

              {!isTalkingVideoPlaying && (<div className='absolute inset-0 flex items-center justify-center bg-black/30 transition-opacity'>
                  <div className='rounded-full bg-white/20 p-4 backdrop-blur-sm'>
                    <PlayIcon className='h-12 w-12 text-white'/>
                  </div>
                </div>)}

              <div className='absolute bottom-0 left-0 right-0 flex items-end justify-between bg-gradient-to-t from-black/80 to-transparent p-4'>
                <div className='text-xs font-semibold uppercase tracking-wide text-white/80'>
                  {t('Talking Avatar Demo')}
                </div>
                <button onClick={toggleTalkingVideoMute} className='rounded-full bg-white/10 p-2 backdrop-blur-sm transition-colors hover:bg-white/20' aria-label={isTalkingVideoMuted ? 'Unmute' : 'Mute'}>
                  {isTalkingVideoMuted ? (<VolumeOffIcon className='h-5 w-5 text-white'/>) : (<VolumeUpIcon className='h-5 w-5 text-white'/>)}
                </button>
              </div>
            </div>
          </div>
        </div>
      </section>

      <VideoSteps />

      {/* User Generated Videos Showcase */}
      <section className='dark relative border-y border-white/[0.06] bg-white/[0.02] py-20 text-slate-200 lg:py-28'>
        <div className='mb-12 px-4 text-center'>
          <h2 className='text-4xl font-extrabold tracking-tight text-white md:text-5xl'>
            {t('Made with Easy-Peasy.AI')}
          </h2>
          <p className='mt-4 text-lg text-slate-400'>
            {t('Explore what our community is creating')}
          </p>
        </div>
        <UserVideos initialVideos={initialVideos} currentVideo='0' useLoadMore={true}/>
      </section>

      <VideoToolLinks />

      <div className='dark'>
        <FeaturedLogos forceDark={true}/>
        <Testimonials forceDark={true}/>
        <VideoFaq />
        <CallToAction preset='ai-videos'/>
        <Footer variant='dark'/>
      </div>
    </main>);
};
export default VideoGenerator;
const getServerSideProps: GetServerSideProps = async () => {
    try {
        const initialVideos = await getPublicVideosPaginated(1, 24);
        return {
            props: {
                initialVideos: initialVideos || [],
            },
        };
    }
    catch (error) {
        console.error('Error fetching videos:', error);
        return {
            props: {
                initialVideos: [],
            },
        };
    }
};

    async function __Next_Translate__getServerSideProps__1a11b03d253__(ctx) {
      if (typeof global !== 'undefined') {
        global.i18nConfig = __i18nConfig
      }
      const res = await getServerSideProps(ctx)
      return {
        
        ...res,
        props: {
          ...(res.props || {}),
          ...(await __loadNamespaces({
            ...ctx,
            ...__i18nConfig,
            pathname: '/ai-video-generator/index',
            loaderName: 'getServerSideProps',
            loadLocaleFrom: __i18nConfig && __i18nConfig.loadLocaleFrom || ((l, n) => import('../../../locales/' + l + '/' + n).then(m => m.default)),
          }))
        }
      }
    }
    export { __Next_Translate__getServerSideProps__1a11b03d253__ as getServerSideProps }
  